import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { unwrapResult } from "@reduxjs/toolkit";
import RenewMotorLayout from "../renewMotorLayout";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import VerifyOtpComponent from "../verifyOtpPage/verifyOtpComponent";
import useQuickRenewalFlow, { RENEW_POLICY_LOGGED_OUT } from "../../../../hooks/useQuickRenewalFlow";
import useMotorForm from "../../../../hooks/useMotorFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { 
  sendLoginOtp, 
  verifyLoginOtp, 
  selectLoginOtpLoading, 
  selectLoginOtpPhoneNumber, 
  selectLoginOtpPinId, 
  selectLoginOtpError,
  clearLoginOtpState 
} from "../../../../redux/features/account/accountOtpSlice";
import { setLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { fetchUserPolicies } from "../../../../redux/features/myPolicySlice";
import { motorRenewalPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { setLoggedInUserDataInLocalStorage } from "../../../../config/config";

export const RENEW_POLICY_CPR_PLATE_NUMBER = "RENEW_POLICY_CPR_PLATE_NUMBER";

const QuickRenewalLoginOtpPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const { handleFlowChange, handlePrevStep } = useQuickRenewalFlow();
  
  const [otp, setOtp] = useState("");
  const [formData, setFormData] = useState({ cprNumber: "", plateNumber: "" });
  const [isVerifying, setIsVerifying] = useState(false);
  
  const isLoading = useSelector(selectLoginOtpLoading);
  const phoneNumber = useSelector(selectLoginOtpPhoneNumber);
  const pinId = useSelector(selectLoginOtpPinId);
  const error = useSelector(selectLoginOtpError);
  
  const { redux_form_data } = useMotorForm(RENEW_POLICY_CPR_PLATE_NUMBER, setFormData);

  useEffect(() => {
    if (redux_form_data) {
      setFormData(redux_form_data);
    }
  }, [redux_form_data]);

  useEffect(() => {
    if (process.env.REACT_APP_SKIP_OTP === "true") return;
    if (!pinId && formData.cprNumber) {
      toast.error(translate("Session expired. Please try again.", "انتهت الجلسة. يرجى المحاولة مرة أخرى."));
      handleFlowChange(RENEW_POLICY_LOGGED_OUT, 0);
    }
  }, [pinId, formData.cprNumber]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const { saveMotorDataInRedux: saveCprPlateData } = useMotorForm(
    RENEW_POLICY_CPR_PLATE_NUMBER,
    setFormData
  );

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      toast.error(translate("Please enter a valid 4-digit OTP", "يرجى إدخال رمز OTP مكون من 4 أرقام"));
      return;
    }

    setIsVerifying(true);

    try {
      const skipOtp = process.env.REACT_APP_SKIP_OTP === "true";
      const skipToken = process.env.REACT_APP_SKIP_OTP_ACCESS_TOKEN;

      let verifyResult;
      if (skipOtp) {
        if (!skipToken) {
          toast.error(translate("OTP skip is enabled but REACT_APP_SKIP_OTP_ACCESS_TOKEN is not set.", "تم تفعيل تخطي رمز التحقق ولكن لم يتم تعيين REACT_APP_SKIP_OTP_ACCESS_TOKEN."));
          return;
        }
        verifyResult = { access_token: skipToken };
      } else {
        const verifyPayload = {
          CPR: formData.cprNumber,
          PinId: pinId,
          Pin: otp,
        };
        const verifyAction = await dispatch(verifyLoginOtp(verifyPayload));
        verifyResult = unwrapResult(verifyAction);
      }

      if (verifyResult) {
        // Save token to Redux and localStorage for Axios interceptor
        dispatch(setLoginTokenInfo(verifyResult));
        setLoggedInUserDataInLocalStorage(verifyResult);
        
        // Now fetch policies with auth token explicitly passed
        const getPoliciesPayload = {
          Cpr: formData.cprNumber,
          Channel: getClientSource(),
          token: verifyResult.access_token,
        };

        const getPoliciesAction = await dispatch(fetchUserPolicies(getPoliciesPayload));
        const getPoliciesResult = unwrapResult(getPoliciesAction);

        if (getPoliciesResult) {
          // Find the renewable policy matching the plate number
          const matchingPolicy = getPoliciesResult.find(
            (policy) =>
              policy.RegistrationNumber == formData.plateNumber &&
              policy.IsRenewable == true &&
              policy.Renewed !== "Yes" &&
              policy.ProductCode === "0101"
          );

          if (matchingPolicy) {
            const policyNumber = matchingPolicy.PolicyNumber;
            
            // Save policy number to form data
            saveCprPlateData({
              ...formData,
              policyNumber: policyNumber,
            });

            // Get premium for the policy
            const premiumPayload = {
              PolicyNumber: policyNumber,
              Channel: getClientSource(),
            };

            const premiumAction = await dispatch(motorRenewalPremium(premiumPayload));
            const premiumResult = unwrapResult(premiumAction);

            if (premiumResult) {
              handleFlowChange(RENEW_POLICY_LOGGED_OUT, 2);
            }
          } else {
            // No renewable policy found - check for any policy with that plate number
            const policyWithPlate = getPoliciesResult.find(
              (policy) => policy.RegistrationNumber == formData.plateNumber
            );

            if (policyWithPlate) {
              toast.error(
                translate(
                  `The Registration number ${formData.plateNumber} is not due for renewal yet.`,
                  `رقم التسجيل ${formData.plateNumber} غير مستحق للتجديد بعد.`
                )
              );
            } else {
              toast.error(
                translate(
                  `No policy found with Registration Number ${formData.plateNumber}.`,
                  `لم يتم العثور على بوليصة برقم اللوحة ${formData.plateNumber}.`
                )
              );
            }
          }
        }
      }
    } catch (e) {
      console.log(e);
      toast.error(translate(e?.ErrorMessageEn || "Verification failed", e?.ErrorMessageAr || "فشل التحقق"));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setOtp("");
      const sendOtpPayload = {
        CPR: formData.cprNumber,
      };
      
      const sendOtpAction = await dispatch(sendLoginOtp(sendOtpPayload));
      unwrapResult(sendOtpAction);
      toast.success(translate("OTP sent successfully", "تم إرسال رمز OTP بنجاح"));
    } catch (e) {
      console.log(e);
      toast.error(translate(e?.ErrorMessageEn || "Failed to resend OTP", e?.ErrorMessageAr || "فشل إعادة إرسال رمز OTP"));
    }
  };

  const handleGoBack = () => {
    dispatch(clearLoginOtpState());
    handlePrevStep();
  };

  const maskedPhone = phoneNumber ? `+973 ${phoneNumber}` : "+973 ********";

  return (
    <RenewMotorLayout
      show_close_btn
      is_hide_back_btn={false}
      btn_onClick={handleVerifyOtp}
      back_btn_onClick={handleGoBack}
      isLoading={isLoading || isVerifying}
      btn_disabled={otp.length !== 4 || isVerifying}
    >
      <div className="max-w-[100%] w-[500px] mx-auto bg-white max767:p-5 max767:pt-0">
        <TitleDescription
          title={t("motorInsurance.renewalPolicyFlow.verifyOtpPage.title")}
          description={t("motorInsurance.renewalPolicyFlow.verifyOtpPage.description")}
        />
        <VerifyOtpComponent
          otp={otp}
          setOtp={setOtp}
          notRecivedCodeHandler={handleResendOtp}
          mobile={maskedPhone}
        />
      </div>
    </RenewMotorLayout>
  );
};

export default QuickRenewalLoginOtpPage;
